import { useEffect, useRef, type RefObject } from 'react'

interface InfiniteScrollSentinelProps {
  rootRef: RefObject<HTMLElement | null>
  hasNextPage?: boolean
  onLoadMore: () => void
  /** Each new page moves the sentinel down — bumping this re-points the observer at it. */
  itemCount: number
}

export const SENTINEL_ROOT_MARGIN = '0px 0px 80% 0px'

/** Calls `onLoadMore` when it scrolls within ~one screen of the bottom of `rootRef`. */
export const InfiniteScrollSentinel = ({
  rootRef,
  hasNextPage,
  onLoadMore,
  itemCount,
}: InfiniteScrollSentinelProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Read the latest callback without re-creating the observer every render.
  const onLoadMoreRef = useRef(onLoadMore)

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore
  }, [onLoadMore])

  useEffect(() => {
    const root = rootRef.current
    const target = sentinelRef.current
    if (!root || !target || !hasNextPage) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) onLoadMoreRef.current()
      },
      { root, rootMargin: SENTINEL_ROOT_MARGIN },
    )
    observer.observe(target)

    return () => observer.disconnect()
  }, [rootRef, hasNextPage, itemCount])

  if (!hasNextPage) return null

  return (
    <div ref={sentinelRef} data-testid='infinite-scroll-sentinel' aria-hidden className='sticky left-0 h-px w-px' />
  )
}
